import { ProjectConstructionType } from '@consigli/types';

import Bridge1440 from '@/assets/images/construction-types/bridge-1440.webp';
import Bridge240 from '@/assets/images/construction-types/bridge-240.webp';
import Bridge480 from '@/assets/images/construction-types/bridge-480.webp';
import Bridge720 from '@/assets/images/construction-types/bridge-720.webp';
import OfficeBuilding1440 from '@/assets/images/construction-types/office-building-1440.webp';
import OfficeBuilding240 from '@/assets/images/construction-types/office-building-240.webp';
import OfficeBuilding480 from '@/assets/images/construction-types/office-building-480.webp';
import OfficeBuilding720 from '@/assets/images/construction-types/office-building-720.webp';
import OtherBuilding1440 from '@/assets/images/construction-types/other-building-1440.webp';
import OtherBuilding240 from '@/assets/images/construction-types/other-building-240.webp';
import OtherBuilding480 from '@/assets/images/construction-types/other-building-480.webp';
import OtherBuilding720 from '@/assets/images/construction-types/other-building-720.webp';
import OtherInfrastructure1440 from '@/assets/images/construction-types/other-infrastructure-1440.webp';
import OtherInfrastructure240 from '@/assets/images/construction-types/other-infrastructure-240.webp';
import OtherInfrastructure480 from '@/assets/images/construction-types/other-infrastructure-480.webp';
import OtherInfrastructure720 from '@/assets/images/construction-types/other-infrastructure-720.webp';
import ResidentialBuilding1440 from '@/assets/images/construction-types/residential-building-1440.webp';
import ResidentialBuilding240 from '@/assets/images/construction-types/residential-building-240.webp';
import ResidentialBuilding480 from '@/assets/images/construction-types/residential-building-480.webp';
import ResidentialBuilding720 from '@/assets/images/construction-types/residential-building-720.webp';
import Road1440 from '@/assets/images/construction-types/road-1440.webp';
import Road240 from '@/assets/images/construction-types/road-240.webp';
import Road480 from '@/assets/images/construction-types/road-480.webp';
import Road720 from '@/assets/images/construction-types/road-720.webp';

export interface ConstructionTypeImage {
  /** The fallback for a browser that ignores `srcSet`. */
  src: string;
  srcSet: string;
}

/**
 * Every illustration ships at 240, 480, 720 and 1440 px wide, so the browser
 * can load one close to the rendered size. Shrinking a much larger file blurs
 * the thin strokes of the line work. 1440 is the Figma frame, kept for a
 * zoomed-in page.
 */
const illustration = (sources: Record<240 | 480 | 720 | 1440, string>): ConstructionTypeImage => ({
  src: sources[720],
  srcSet: Object.entries(sources)
    .map(([width, source]) => `${source} ${width}w`)
    .join(', '),
});

const bridgeImage = illustration({
  240: Bridge240,
  480: Bridge480,
  720: Bridge720,
  1440: Bridge1440,
});
const officeBuildingImage = illustration({
  240: OfficeBuilding240,
  480: OfficeBuilding480,
  720: OfficeBuilding720,
  1440: OfficeBuilding1440,
});
const otherBuildingImage = illustration({
  240: OtherBuilding240,
  480: OtherBuilding480,
  720: OtherBuilding720,
  1440: OtherBuilding1440,
});
const otherInfrastructureImage = illustration({
  240: OtherInfrastructure240,
  480: OtherInfrastructure480,
  720: OtherInfrastructure720,
  1440: OtherInfrastructure1440,
});
const residentialBuildingImage = illustration({
  240: ResidentialBuilding240,
  480: ResidentialBuilding480,
  720: ResidentialBuilding720,
  1440: ResidentialBuilding1440,
});
const roadImage = illustration({
  240: Road240,
  480: Road480,
  720: Road720,
  1440: Road1440,
});

/**
 * Typed as a total Record, so adding a ProjectConstructionType without an
 * illustration is a compile error rather than something a test has to notice.
 */
const IMAGE_BY_CONSTRUCTION_TYPE: Record<ProjectConstructionType, ConstructionTypeImage> = {
  [ProjectConstructionType.OFFICE_BUILDING]: officeBuildingImage,
  [ProjectConstructionType.RESIDENTIAL_BUILDING]: residentialBuildingImage,
  [ProjectConstructionType.HOSPITAL]: otherBuildingImage,
  [ProjectConstructionType.DATA_CENTER]: otherBuildingImage,
  [ProjectConstructionType.OTHER_BUILDING]: otherBuildingImage,
  [ProjectConstructionType.BRIDGE]: bridgeImage,
  [ProjectConstructionType.ROAD]: roadImage,
  [ProjectConstructionType.HOTEL]: otherBuildingImage,
  [ProjectConstructionType.OTHER_INFRASTRUCTURE]: otherInfrastructureImage,
};

/**
 * The card illustration for a project's first construction type.
 *
 * `constructionTypes` is a required array, so TypeScript will not flag `[0]` as
 * possibly undefined — the fallback is there for the empty array it permits.
 */
export const constructionTypeImage = (
  constructionTypes: ProjectConstructionType[],
): ConstructionTypeImage => IMAGE_BY_CONSTRUCTION_TYPE[constructionTypes[0]] ?? officeBuildingImage;
